import { Clock, Heart } from 'lucide-react';
import { BlogPost } from '../types';
import { Link } from 'react-router-dom';

interface BlogCardProps {
  post: BlogPost;
}

export function BlogCard({ post }: BlogCardProps) {
  return (
    <article className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
      {/* Decorative corner shape */}
      <div className="absolute top-0 right-0 w-16 h-16 bg-pink-100/30 clip-path-triangle transform rotate-90" />
      
      <Link to={`/post/${post.id}`} className="relative block">
        {/* Image container with overlay effect */}
        <div className="relative overflow-hidden">
          <img 
            src={post.imageUrl} 
            alt={post.title}
            className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {/* Image overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-pink-100/20 to-blue-100/20" />
          
          {/* Category ribbon */}
          <span className="absolute top-4 -left-1 bg-pink-200 text-pink-800 px-4 py-1 text-sm font-medium shadow-sm 
            skew-x-6 hover:skew-x-0 transition-transform">
            {post.category || 'Design'}
          </span>
        </div>

        <div className="p-6 space-y-4">
          {/* Title with gradient text */}
          <h2 className="text-2xl font-bold bg-gradient-to-r from-pink-400 to-blue-500 bg-clip-text text-transparent">
            {post.title}
          </h2>

          {/* Excerpt with decorative underline */}
          <p className="text-gray-600 relative pb-4 
            after:absolute after:bottom-0 after:left-0 after:w-12 after:h-1 after:bg-pink-200 after:rounded-full">
            {post.excerpt}
          </p>

          {/* Footer with creative layout */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-2">
              {/* Author with heart icon */}
              <span className="bg-pink-100 text-pink-600 px-3 py-1 rounded-full flex items-center">
                <Heart className="w-4 h-4 mr-2 fill-current" />
                {post.author}
              </span>
            </div>
            
            {/* Read time with animated clock */}
            <div className="flex items-center text-blue-600 space-x-1">
              <Clock className="w-4 h-4 transition-transform group-hover:rotate-12" />
              <span className="font-medium">{post.readTime}</span>
            </div>
          </div>
        </div>

        {/* Hover sparkle effect */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300
            before:absolute before:top-1/4 before:left-1/2 before:w-24 before:h-24 before:bg-gradient-to-r 
            before:from-pink-200/30 before:via-transparent before:to-blue-200/30 before:animate-sparkle" />
        </div>
      </Link>
    </article>
  );
}

// Add this CSS for the triangle clip-path
<style jsx global>{`
  .clip-path-triangle {
    clip-path: polygon(100% 0, 0% 100%, 100% 100%);
  }
`}</style>